package com.heelion.farmafreelance.voice

import androidx.compose.runtime.Composable

/**
 * Sintesi vocale locale, controparte di [VoiceRecognizer]: usa solo le API di sistema/browser
 * (Android TextToSpeech, Web Speech API speechSynthesis). Nessun servizio esterno, nessun costo.
 *
 * Dove non è disponibile (Desktop) [isAvailable] torna false e l'assistente risponde solo a
 * testo, come prima.
 */
interface VoiceSpeaker {
    fun isAvailable(): Boolean

    /**
     * Legge [testo] in italiano. Di norma interrompe un'eventuale lettura in corso; con
     * [accoda] = true lo legge invece dopo (es. il promemoria dopo la presentazione).
     */
    fun speak(testo: String, accoda: Boolean = false)

    /** Interrompe la lettura (es. prima di riaprire il microfono, per non ascoltare se stessi). */
    fun stop()

    /** Voci italiane tra cui l'utente può scegliere quella di Farmy (vuota se non si può scegliere). */
    fun voci(): List<VoceDisponibile> = emptyList()

    /** Usa la voce con questo [VoceDisponibile.id]; null = scelta automatica (maschile se c'è). */
    fun usaVoce(id: String?) {}

    /** Id della voce scelta dall'utente, null se automatica. */
    fun voceScelta(): String? = null
}

/** Una voce di sistema/browser selezionabile, con un nome comprensibile per l'utente. */
data class VoceDisponibile(val id: String, val etichetta: String)

/**
 * Web: da "nome1\nnome2\u0001..." (\u0001 = voce online, non locale) alle voci del selettore.
 * Condivisa tra build JS e Wasm, che leggono l'elenco dal browser allo stesso modo.
 */
internal fun etichetteVociWeb(nomi: String): List<VoceDisponibile> =
    nomi.split('\n').filter { it.isNotBlank() }.map { riga ->
        val online = riga.endsWith('\u0001')
        val nome = riga.removeSuffix("\u0001")
        VoceDisponibile(nome, nome + if (online) " · alta qualità, serve internet" else "")
    }

/** Frase di prova letta quando si sceglie una voce nel selettore. */
const val FRASE_PROVA_VOCE = "Ciao, sono Farmy! Ti piace questa voce?"

@Composable
expect fun rememberVoiceSpeaker(): VoiceSpeaker

/**
 * Carattere della voce di Farmy, uguale su Android e web: una voce maschile un po' più grave e
 * pacata del normale, autorevole e non "da cartone animato". 1.0 = voce naturale; la tonalità va
 * da 0.5 a 2.0: sotto ~0.8 diventa innaturale, sopra ~1.3 suona infantile.
 */
const val TONALITA_VOCE = 0.9f
const val VELOCITA_VOCE = 0.95f

/**
 * Web: nomi delle voci italiane maschili più diffuse nei browser (Windows/Edge: Diego, Cosimo,
 * Giuseppe; Mac/iOS: Luca). "Google italiano" di Chrome è femminile: se non c'è una di queste
 * resta la voce predefinita, sempre con tonalità e velocità di Farmy.
 */
const val REGEX_VOCI_MASCHILI_WEB = "diego|cosimo|giuseppe|luca|paolo|roberto|\\bmale\\b"

/**
 * Web: voci "di qualità" (neurali, online). Le voci locali di Windows (es. "Microsoft Cosimo") sono
 * vecchie e robotiche e il browser le elenca per prime: senza questa preferenza Farmy prendeva
 * Cosimo invece di "Microsoft Diego Online (Natural)" di Edge. Su queste voci la tonalità resta
 * 1.0: abbassarla le rende metalliche.
 */
const val REGEX_VOCI_QUALITA_WEB = "natural|online|neural|google"

/**
 * Adatta un testo pensato per lo schermo alla lettura ad alta voce: via elenchi puntati,
 * virgolette e a capo, che i motori TTS leggerebbero male o come pause strane.
 */
fun testoPerVoce(testo: String): String = dateAParole(testo)
    .replace("\n", " ")
    .replace(Regex("[•«»\"]"), "")
    .replace(Regex("\\s+"), " ")
    .trim()

private val DATA_CON_BARRA = Regex("(?<![\\d/])(\\d{1,2})/(\\d{1,2})(?:/(\\d{4}))?(?![\\d/])")
private val MESI_VOCE = listOf("gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
    "agosto", "settembre", "ottobre", "novembre", "dicembre")

/**
 * "5/10" -> "5 ottobre", "1/11/2026" -> "primo novembre 2026": la voce leggerebbe "5/10" come
 * una frazione ("cinque decimi"). Sullo schermo le date restano brevi. Solo giorni e mesi validi.
 */
internal fun dateAParole(testo: String): String = DATA_CON_BARRA.replace(testo) { m ->
    val giorno = m.groupValues[1].toInt()
    val mese = m.groupValues[2].toInt()
    if (giorno !in 1..31 || mese !in 1..12) return@replace m.value
    val anno = m.groupValues[3].takeIf { it.isNotEmpty() }?.let { " $it" }.orEmpty()
    (if (giorno == 1) "primo" else giorno.toString()) + " " + MESI_VOCE[mese - 1] + anno
}
